(() => {
  'use strict';

  const COMPONENT_NAME = 'threshold-gift';
  const GIFT_PROPERTY = '_threshold_gift';
  const FLASH_PREFIX = 'tekevolution-threshold-gift:';

  if (customElements.get(COMPONENT_NAME)) return;

  const getCartUrl = () => {
    if (window.routes?.cart_url) return window.routes.cart_url;
    return `${window.Shopify?.routes?.root || '/'}cart`;
  };

  class ThresholdGift extends HTMLElement {
    constructor() {
      super();

      this.isBusy = false;
      this.checkoutBypass = false;
      this.reconcileTimer = null;

      this.handleAddClick = this.handleAddClick.bind(this);
      this.handleCheckoutClick = this.handleCheckoutClick.bind(this);
    }

    connectedCallback() {
      if (this.isConnectedOnce) return;
      this.isConnectedOnce = true;

      this.blockId = this.dataset.blockId;
      this.addButton = this.querySelector('[data-threshold-gift-add]');
      this.statusElement = this.querySelector('[data-threshold-gift-status]');
      this.buttonLabel = this.querySelector('[data-threshold-gift-button-label]');
      this.spinner = this.querySelector('[data-threshold-gift-spinner]');

      this.addButton?.addEventListener('click', this.handleAddClick);
      document.addEventListener('click', this.handleCheckoutClick, true);

      this.restoreFlashMessage();
      this.reconcileTimer = window.setTimeout(() => this.reconcile(), 0);
    }

    disconnectedCallback() {
      this.addButton?.removeEventListener('click', this.handleAddClick);
      document.removeEventListener('click', this.handleCheckoutClick, true);
      window.clearTimeout(this.reconcileTimer);
      this.isConnectedOnce = false;
    }

    getState(element = this) {
      const giftLines = [
        ...element.querySelectorAll('[data-widget-gift-line]'),
      ].map((line) => ({
        key: line.dataset.lineKey || '',
        quantity: Number(line.dataset.lineQuantity || 0),
        discounted: line.dataset.lineDiscounted === 'true',
      })).filter((line) => line.key);

      return {
        eligible: element.dataset.eligible === 'true',
        anyGiftPresent: element.dataset.anyGiftPresent === 'true',
        widgetGiftPresent: giftLines.length > 0,
        giftLines,
        giftAvailable: element.dataset.giftAvailable === 'true',
        giftVariantId: Number(element.dataset.giftVariantId || 0),
      };
    }

    getSections() {
      const sections = ['cart-drawer', 'cart-icon-bubble'];

      if (document.getElementById('main-cart-items')) {
        sections.push('main-cart-items');
      }

      if (document.getElementById('main-cart-footer')) {
        sections.push('main-cart-footer');
      }

      return sections;
    }

    getRequestBody(extraData) {
      return JSON.stringify({
        ...extraData,
        sections: this.getSections(),
        sections_url: window.location.pathname,
      });
    }

    async fetchJson(url, options) {
      const response = await fetch(url, options);
      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          result.description ||
          result.message ||
          this.dataset.errorMessage ||
          'Non è stato possibile aggiornare il carrello.'
        );
      }

      return result;
    }

    async getFreshState() {
      const sectionUrl = new URL(getCartUrl(), window.location.origin);
      sectionUrl.searchParams.set('section_id', 'cart-drawer');
      sectionUrl.searchParams.set('_threshold_gift', String(Date.now()));

      const response = await fetch(sectionUrl.toString(), {
        headers: {
          Accept: 'text/html',
        },
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error('Non è stato possibile verificare il carrello.');
      }

      const html = await response.text();
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const freshWidget = [...parsedDocument.querySelectorAll(COMPONENT_NAME)]
        .find((widget) => widget.dataset.blockId === this.blockId);

      if (!freshWidget) {
        throw new Error('La configurazione dell’omaggio non è disponibile.');
      }

      return this.getState(freshWidget);
    }

    async reconcile() {
      if (this.isBusy) return;

      const state = this.getState();
      if (!state.widgetGiftPresent) return;

      if (!state.eligible) {
        this.storeFlashMessage(
          'L’omaggio è stato rimosso perché il carrello è sceso sotto la soglia.'
        );
        await this.normalizeGiftLines(state.giftLines);
        return;
      }

      if (
        state.giftLines.length !== 1 ||
        state.giftLines[0].quantity !== 1
      ) {
        this.storeFlashMessage(
          'La quantità dell’omaggio è stata riportata automaticamente a una unità.'
        );
        await this.normalizeGiftLines(state.giftLines, true);
        return;
      }

      if (!state.giftLines[0].discounted) {
        this.storeFlashMessage(
          'L’omaggio non risultava gratuito ed è stato rimosso. Verifica lo sconto automatico Shopify.'
        );
        await this.normalizeGiftLines(state.giftLines);
      }
    }

    async handleAddClick() {
      if (this.isBusy) return;

      this.setBusy(true);
      this.showStatus('');

      try {
        const state = await this.getFreshState();

        if (!state.giftAvailable) {
          throw new Error('L’omaggio è momentaneamente esaurito.');
        }

        if (!state.eligible) {
          throw new Error('Il carrello non raggiunge più la soglia prevista.');
        }

        if (state.anyGiftPresent) {
          throw new Error('Il prodotto omaggio è già presente nel carrello.');
        }

        const result = await this.fetchJson(
          `${window.Shopify?.routes?.root || '/'}cart/add.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: this.getRequestBody({
              id: state.giftVariantId,
              quantity: 1,
              properties: {
                [GIFT_PROPERTY]: '1',
              },
            }),
          }
        );

        this.renderCart(result);
      } catch (error) {
        console.error('[Threshold gift]', error);
        this.showStatus(error.message || this.dataset.errorMessage);
      } finally {
        if (this.isConnected) this.setBusy(false);
      }
    }

    async normalizeGiftLines(giftLines, replace = false) {
      if (this.isBusy || !giftLines.length) return;

      this.setBusy(true);

      try {
        await this.normalizeGiftLinesWhileBusy(giftLines, replace);
      } catch (error) {
        console.error('[Threshold gift]', error);
        this.showStatus(error.message || this.dataset.errorMessage);
      } finally {
        if (this.isConnected) this.setBusy(false);
      }
    }

    async handleCheckoutClick(event) {
      const checkout = event.target.closest('#CartDrawer-Checkout, #checkout');
      if (!checkout || this.checkoutBypass) return;

      const isCartCheckout =
        checkout.closest('cart-drawer') ||
        checkout.closest('#main-cart-footer');

      if (!isCartCheckout || !this.getState().widgetGiftPresent) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.isBusy) return;
      this.setBusy(true);
      this.showStatus('');

      try {
        const state = await this.getFreshState();

        if (state.widgetGiftPresent) {
          if (!state.eligible) {
            this.storeFlashMessage(
              'L’omaggio è stato rimosso perché il carrello è sceso sotto la soglia.'
            );
            await this.normalizeGiftLinesWhileBusy(state.giftLines);
            return;
          }

          if (
            state.giftLines.length !== 1 ||
            state.giftLines[0].quantity !== 1
          ) {
            this.storeFlashMessage(
              'La quantità dell’omaggio è stata riportata automaticamente a una unità.'
            );
            await this.normalizeGiftLinesWhileBusy(state.giftLines, true);
            return;
          }

          if (!state.giftLines[0].discounted) {
            this.storeFlashMessage(
              'L’omaggio non risultava gratuito ed è stato rimosso. Verifica lo sconto automatico Shopify.'
            );
            await this.normalizeGiftLinesWhileBusy(state.giftLines);
            return;
          }
        }

        this.checkoutBypass = true;

        if (checkout instanceof HTMLAnchorElement) {
          window.location.assign(checkout.href);
          return;
        }

        checkout.click();
        window.setTimeout(() => {
          this.checkoutBypass = false;
        }, 0);
      } catch (error) {
        console.error('[Threshold gift]', error);
        this.showStatus(
          'Non è stato possibile verificare l’omaggio. Rimuovilo oppure riprova prima del checkout.'
        );
      } finally {
        if (this.isConnected) this.setBusy(false);
      }
    }

    async normalizeGiftLinesWhileBusy(giftLines, replace = false) {
      const updates = {};

      giftLines.forEach((line) => {
        if (line.key) updates[line.key] = 0;
      });

      let result = await this.fetchJson(
        `${window.Shopify?.routes?.root || '/'}cart/update.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: this.getRequestBody({
            updates,
          }),
        }
      );

      if (replace) {
        const state = await this.getFreshState();

        if (!state.eligible || !state.giftAvailable) {
          this.renderCart(result);
          return;
        }

        result = await this.fetchJson(
          `${window.Shopify?.routes?.root || '/'}cart/add.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: this.getRequestBody({
              id: state.giftVariantId,
              quantity: 1,
              properties: {
                [GIFT_PROPERTY]: '1',
              },
            }),
          }
        );
      }

      this.renderCart(result);
    }

    renderCart(result) {
      if (!result.sections?.['cart-drawer']) {
        window.location.reload();
        return;
      }

      const cartDrawer = document.querySelector('cart-drawer');

      if (typeof cartDrawer?.renderContents === 'function') {
        cartDrawer.renderContents(result, true);
      } else {
        const parsedDrawer = new DOMParser().parseFromString(
          result.sections['cart-drawer'],
          'text/html'
        );
        const nextDrawer = parsedDrawer.querySelector('#CartDrawer');
        const currentDrawer = document.getElementById('CartDrawer');

        if (nextDrawer && currentDrawer) {
          currentDrawer.innerHTML = nextDrawer.innerHTML;
        }
      }

      this.replaceSectionContent(
        result.sections['main-cart-items'],
        '#main-cart-items'
      );
      this.replaceSectionContent(
        result.sections['main-cart-footer'],
        '#main-cart-footer'
      );

      document.dispatchEvent(
        new CustomEvent('shopify:cart:lines-update', {
          bubbles: true,
          detail: {
            cart: result,
            source: 'threshold-gift',
          },
        })
      );
    }

    replaceSectionContent(sectionHtml, selector) {
      if (!sectionHtml) return;

      const currentElement = document.querySelector(selector);
      if (!currentElement) return;

      const parsedDocument = new DOMParser().parseFromString(
        sectionHtml,
        'text/html'
      );
      const nextElement = parsedDocument.querySelector(selector);

      if (nextElement) {
        currentElement.innerHTML = nextElement.innerHTML;
      }
    }

    setBusy(busy) {
      this.isBusy = busy;
      this.toggleAttribute('aria-busy', busy);

      if (this.addButton) {
        this.addButton.disabled = busy;
      }

      this.buttonLabel?.classList.toggle('hidden', busy);
      this.spinner?.classList.toggle('hidden', !busy);
    }

    showStatus(message) {
      if (!this.statusElement) return;

      this.statusElement.textContent = message || '';
      this.statusElement.hidden = !message;

      const pageCartErrors = document.getElementById('cart-errors');
      if (pageCartErrors && message) {
        pageCartErrors.textContent = message;
      }
    }

    getFlashKey() {
      return `${FLASH_PREFIX}${this.blockId}`;
    }

    storeFlashMessage(message) {
      try {
        sessionStorage.setItem(this.getFlashKey(), message);
      } catch (_error) {
        // Il messaggio è accessorio; la protezione del carrello resta attiva.
      }
    }

    restoreFlashMessage() {
      try {
        const message = sessionStorage.getItem(this.getFlashKey());
        if (!message) return;

        sessionStorage.removeItem(this.getFlashKey());
        this.showStatus(message);
      } catch (_error) {
        // Session storage può non essere disponibile in alcuni browser.
      }
    }
  }

  customElements.define(COMPONENT_NAME, ThresholdGift);
})();
